/**
 * "Volver al destino": al pedir una pantalla interna sin sesión se manda al login con
 * `?next=<ruta+query>`, y tras iniciar sesión se regresa ahí. `next` viene de la URL
 * (controlable por un atacante), así que solo se acepta una ruta interna segura.
 */

/** Pantallas de acceso que nunca son un destino válido. */
const EXCLUDED_SEGMENTS = new Set(['login', 'register', 'accept-invite', 'select-role', 'account-inactive']);

function segments(path: string): string[] {
  return path.split(/[?#]/)[0].split('/').filter(Boolean);
}

/** ¿Es una ruta de login/registro/invitación (no tiene sentido guardarla como destino)? */
export function isAuthRoute(pathname: string): boolean {
  const segs = segments(pathname).map((s) => s.toLowerCase());
  if (segs.length === 0) return false;
  return EXCLUDED_SEGMENTS.has(segs[0]) || (segs.length > 1 && EXCLUDED_SEGMENTS.has(segs[1]));
}

/** URL de login que conserva el destino. Sin destino útil, devuelve el login simple. */
export function buildLoginUrl(pathname: string, search = '', tenantId?: string | null): string {
  const base = tenantId ? `/${encodeURIComponent(tenantId)}/login` : '/login';
  if (!pathname || pathname === '/' || isAuthRoute(pathname)) return base;
  return `${base}?next=${encodeURIComponent(`${pathname}${search}`)}`;
}

/**
 * Devuelve `next` solo si es una ruta interna relativa segura de la empresa `tenantCode`;
 * si no, null (el llamador cae al destino por defecto).
 */
export function resolveSafeNext(next: string | null | undefined, tenantCode: string | null | undefined): string | null {
  if (!next || !tenantCode) return null;
  if (!next.startsWith('/') || next.startsWith('//')) return null;
  // Barras invertidas y caracteres de control: los navegadores los normalizan a "//" o esquemas.
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(next)) return null;
  let parsed: URL;
  try {
    parsed = new URL(next, 'http://internal.invalid');
  } catch {
    return null;
  }
  if (parsed.origin !== 'http://internal.invalid') return null;
  const segs = segments(parsed.pathname);
  if (segs.length < 2) return null; // "/" o "/<empresa>" sola no es una pantalla concreta
  let first: string;
  try { first = decodeURIComponent(segs[0]); } catch { return null; }
  if (first !== tenantCode) return null;
  if (isAuthRoute(parsed.pathname)) return null;
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
